import React from 'react';
import { motion } from 'framer-motion';

// Icon imports remain grouped for readability.
import { 
    Code, 
    Storage, 
    Build, 
    Psychology, 
    Chat, 
    Visibility, 
    AutoAwesome 
} from '@mui/icons-material';

// The data is updated with more descriptive, professional content.
const technicalSkills = [
    { 
        Icon: Code, 
        category: 'Front-End Development', 
        description: 'Proficient in React, JavaScript (ES6+), HTML5, and SCSS for creating responsive and dynamic user interfaces.' 
    },
    { 
        Icon: Storage, 
        category: 'Back-End Development', 
        description: 'Experienced with Node.js, Express, and MongoDB for building robust server-side logic and APIs.' 
    },
    { 
        Icon: Build, 
        category: 'Developer Tools & Platforms', 
        description: 'Skilled in using Git for version control, implementing RESTful APIs, and deploying on platforms like Netlify and Railway.' 
    }
];

const professionalStrengths = [
    { 
        Icon: Psychology, 
        skill: 'Analytical Problem-Solving',
        description: 'A systematic approach to dissecting challenges and implementing effective solutions.'
    },
    { 
        Icon: Chat, 
        skill: 'Clear & Collaborative Communication',
        description: 'Ensuring alignment with team members and stakeholders to achieve project goals.'
    },
    { 
        Icon: Visibility, 
        skill: 'Meticulous Attention to Detail',
        description: 'A commitment to precision in both code and design for a polished final product.'
    },
    { 
        Icon: AutoAwesome, 
        skill: 'Continuous Learning & Adaptability',
        description: 'Proactively staying current with industry trends and mastering new tools.'
    }
];

const About = () => {
  return (
    <section id="about" className="about-wrapper">
      <motion.div
        className="about-container"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
        viewport={{ once: true }}
      >
        <div className="about-content">
          <h2 className="about-title">About Me</h2>
          
          {/* --- Updated "About Me" Narrative --- */}
          <p className="about-intro">
            I'm <strong>Jalen Navarro</strong>, a Full Stack Developer based in the Philippines, specializing in building high-performance and user-centric web applications. My passion lies in transforming complex problems into elegant, efficient, and scalable code.
          </p>
          <p className="about-narrative">
            My experience includes developing a diverse range of projects, from dynamic business websites to robust applications with complete CRUD functionality and real-time features like chat systems. I have a proven ability to architect and implement both the front-end and back-end, ensuring a seamless and secure user experience from start to finish.
          </p>
          <p className="about-philosophy">
            I believe that the best digital products are born from a commitment to more than just functional code. I prioritize clean design, intuitive user journeys, and resilient architecture. As a dedicated problem-solver and a strong communicator, I thrive on continuously learning new technologies and collaborating to bring impactful ideas to life.
          </p>
        </div>

        <div className="skills-section">
          {/* --- Updated Technical Skills --- */}
          <div className="skills-column">
            <h3 className="skills-heading">Technical Expertise</h3>
            <ul className="skills-list descriptive-list">
              {technicalSkills.map((skill, index) => (
                <li key={index}>
                  <skill.Icon fontSize="small" /> 
                  <div>
                    <strong>{skill.category}</strong>
                    <p>{skill.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* --- Updated Professional Strengths --- */}
          <div className="skills-column">
            <h3 className="skills-heading">Professional Strengths</h3>
            <ul className="skills-list descriptive-list">
              {professionalStrengths.map((skill, index) => (
                <li key={index}>
                  <skill.Icon fontSize="small" />
                  <div>
                    <strong>{skill.skill}</strong>
                    <p>{skill.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default About;